import React, { useState, useEffect } from "react";
import ApiService from '../Components/apiService';
import { BASE_URL } from '../Components/Constant';
import {
  Table,
  Typography,
  Tooltip,
  message,
  Row,
  Col,
  Tabs,
  Popconfirm
} from 'antd';
import {
  RedoOutlined,
  DeleteOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { convertDateToUserTimezone } from '../Components/Utility';
import { DateTimeFormat } from '../Components/Constant';


const { Title } = Typography;
const { TabPane } = Tabs;

const Recyclebin = () => {
  const [deletedFields, setDeletedFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [deletedRecords, setDeletedRecords] = useState([]);
      const [loadingData,setLoadingData]=useState(false);


  const fetchDeleteFields = async () => {
    setLoading(true);
    const apiService = new ApiService(
      `${BASE_URL}/deleted_fields`,
      { 'Content-Type': 'application/json' },
      'GET'
    );
    try {
      const response = await apiService.makeCall();
      setDeletedFields(response);
    } catch (error) {
      const errorMessage = error && typeof error === 'object'
        ? Object.entries(error).map(([key, value]) =>
          `${key}: ${Array.isArray(value) ? value.join(', ') : value}`
        ).join(' | ')
        : 'Failed to fetch fields due to an unknown error';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };


  const fetchDeleteRecords = async () => {
    setLoadingData(true);
    const apiService = new ApiService(
      `${BASE_URL}/get_deleted_records`,
      { 'Content-Type': 'application/json' },
      'GET'
    );
    try {
      const response = await apiService.makeCall();
      setDeletedRecords(response);
    } catch (error) {
      const errorMessage = error && typeof error === 'object'
        ? Object.entries(error).map(([key, value]) =>
          `${key}: ${Array.isArray(value) ? value.join(', ') : value}`
        ).join(' | ')
        : 'Failed to fetch due to an unknown error';
      message.error(errorMessage);
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    fetchDeleteFields();
    fetchDeleteRecords();
  }, []);

  const handleRestore = async (field) => {
    try {
      const apiService = new ApiService(
        `${BASE_URL}/${field.field_id}/restore`,
        {},
        'POST'
      );
      await apiService.makeCall();
      message.success('Field restored successfully.');
      fetchDeleteFields();
    } catch (error) {
      const errorMessage = error && typeof error === 'object'
        ? Object.entries(error).map(([key, value]) =>
          `${key}: ${Array.isArray(value) ? value.join(', ') : value}`
        ).join(' | ')
        : 'Failed to restore field due to an unknown error';
      message.error(errorMessage);
    }
  };

  const handleRestoreRecord = async (record) => {

     if(record.is_cascade_deleted===true){
      message.error("This record is linked to a parent. Please restore the parent record first.");
      return;
    }
    try {
      setLoadingData(true);
      const apiService = new ApiService(
        `${BASE_URL}/restore_record/${record.object_name}/${record.record_id}`,
        {},
        'POST'
      );
      await apiService.makeCall();
      message.success('Record restored successfully.');
      fetchDeleteRecords();
    } catch (error) {
      const errorMessage = error && typeof error === 'object'
        ? Object.entries(error).map(([key, value]) =>
          `${key}: ${Array.isArray(value) ? value.join(', ') : value}`
        ).join(' | ')
        : 'Failed to restore record due to an unknown error';
      message.error(errorMessage);
    }
  };


  const handledeleteRecordPermanent = async (record) => {
    try {
      setLoadingData(true);

      const apiService = new ApiService(
        `${BASE_URL}/hard_delete_record/${record.object_name}/${record.record_id}`,
        {},
        'DELETE'
      );
      await apiService.makeCall();
      message.success('Record Deleted Successfully');
      fetchDeleteRecords();
    } catch (error) {
      const errorMessage = error && typeof error === 'object'
        ? Object.entries(error).map(([key, value]) =>
          `${key}: ${Array.isArray(value) ? value.join(', ') : value}`
        ).join(' | ')
        : 'Failed to delete record due to an unknown error';
      message.error(errorMessage);
    }


  }

  const columns = [
    {
      title: 'Field API Name',
      dataIndex: 'field_name',
      key: 'field_name',
      sorter: (a, b) => a.field_name.localeCompare(b.field_name),
    },
    {
      title: 'Object Name',
      dataIndex: 'object_name',
      key: 'object_name',
      sorter: (a, b) => a.object_name.localeCompare(b.object_name),
    },
    {
      title: 'Action',
      key: 'operation',
      render: (_, record) => (
        <>
          <Tooltip title="Restore" onClick={() => handleRestore(record)}>
            <RedoOutlined
              style={{ marginRight: 5, fontSize: '12px', cursor: 'pointer' }}
            />
          </Tooltip>
          <Tooltip title="Fields will automatically delete after 7 days">
            <QuestionCircleOutlined
              style={{ marginRight: 5, fontSize: '12px', cursor: 'pointer' }}
            />
          </Tooltip>
        </>
      ),
    },
  ];


  const columnsForData = [
  {
    title: 'Name',
    dataIndex: 'name',
    key: 'name',
    width: 150, 
    ellipsis: true, 
  },
  {
    title: 'Object Name',
    dataIndex: 'object_name',
    key: 'object_name',
    sorter: (a, b) => a.object_name.localeCompare(b.object_name),
    width: 180,
    ellipsis: true,
  },
  {
    title: 'Deleted by',
    dataIndex: 'deleted_by',
    key: 'deleted_by',
    sorter: (a, b) => a.deleted_by?.Name?.localeCompare(b.deleted_by?.name),
    render: (deleted_by) => deleted_by?.name || '',
    width: 180,
    ellipsis: true,
  },
  {
    title: 'Deleted at',
    dataIndex: 'deleted_at',
    key: 'deleted_at',
    sorter: (a, b) => new Date(a.deleted_at) - new Date(b.deleted_at),
    render: (text) =>
      text && dayjs(text).isValid()
        ? convertDateToUserTimezone(text, DateTimeFormat)
        : '',
    width: 200,
    ellipsis: true,
  },
  {
    title: 'Action',
    key: 'operation',
    width: 120,
    render: (_, record) => (
      <>
        <Tooltip title="Restore" onClick={() => handleRestoreRecord(record)}>
          <RedoOutlined
            style={{ marginRight: 5, fontSize: '12px', cursor: 'pointer' }}
          />
        </Tooltip>
        <Tooltip title="Permanently Delete Record">
          <Popconfirm
            title="Are you sure you want to permanently delete this item? This action cannot be undone."
            onConfirm={() => handledeleteRecordPermanent(record)}
            okText="Yes"
            cancelText="No"
          >
            <DeleteOutlined
              style={{
                color: 'red',
                marginRight: 0,
                fontSize: '12px',
                cursor: 'pointer',
              }}
            />
          </Popconfirm>
        </Tooltip>
      </>
    ),
  },
];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 5 }}>
        <Col>
          <Title level={4} style={{ marginTop: '10px' }}>Recycle Bin</Title>
        </Col>
      </Row>

      <Tabs defaultActiveKey="1">
        <TabPane tab="Metadata" key="1">
          <Table
            dataSource={deletedFields}
            columns={columns}
            loading={loading}
            rowKey="id"
            style={{ marginTop: 20 }}
            scroll={{ x: '1000', y: 550 }}
            pagination={{
              pageSize: pageSize,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              onChange: (page, pageSize) => {
                setPageSize(pageSize);
              },
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} items`,
            }}
          />
        </TabPane>

        <TabPane tab="Data" key="2">
          <Table
            dataSource={deletedRecords}
            columns={columnsForData}
            loading={loadingData}
            rowKey="id"
            style={{ marginTop: 20 }}
            scroll={{ x: 'max-content', y: 550 }}
            pagination={{
              pageSize: pageSize,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              onChange: (page, pageSize) => {
                setPageSize(pageSize);
              },
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} items`,
            }}
          />
        </TabPane>
      </Tabs>
    </div>
  );
};

export default Recyclebin;
